/**
 * QR Codes Stats — overall analytics dashboard.
 * Screen code: QR-004
 */
import { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { motion } from 'framer-motion';
import axios from 'axios';
import { toast } from 'sonner';
import {
  Loader2, BarChart3, ScanLine, CalendarDays, TrendingUp, QrCode, Trophy,
} from 'lucide-react';
import DashboardLayout from '../../components/DashboardLayout';
import { useLanguage } from '../../context/LanguageContext';
import { getAuthHeaders } from '../../utils/authHeaders';

const API = process.env.REACT_APP_BACKEND_URL;

export default function QRCodesStats() {
  const navigate = useNavigate();
  const { language } = useLanguage();
  const isAr = language === 'ar';
  const t = (ar, en) => (isAr ? ar : en);

  const [stats, setStats]     = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    (async () => {
      try {
        const res = await axios.get(
          `${API}/api/admin/qr-codes/stats/overall`,
          { headers: getAuthHeaders() },
        );
        setStats(res.data);
      } catch (err) {
        toast.error(t('فشل تحميل الإحصائيات', 'Stats load failed'));
      } finally {
        setLoading(false);
      }
    })();
  }, []);

  const breadcrumb = [
    { labelAr: 'لوحة التحكم',   labelEn: 'Dashboard',       path: '/dashboard'         },
    { labelAr: 'نظام QR Code',  labelEn: 'QR Code System',  path: '/qr-codes'          },
    { labelAr: 'الإحصائيات',    labelEn: 'Statistics'                                    },
  ];

  const maxDailyCount = Math.max(1, ...(stats?.daily_scans || []).map((d) => d.count));

  return (
    <DashboardLayout
      title="إحصائيات QR"
      titleEn="QR Statistics"
      subtitle="نظرة شاملة على أداء رموزك — مسحات، اتجاهات، الأكثر نجاحاً."
      subtitleEn="Full overview of your codes — scans, trends, top performers."
      screenCode="QR-004"
      backPath="/qr-codes"
      onBack={() => navigate('/qr-codes')}
      breadcrumb={breadcrumb}
    >
      <section
        className="max-w-6xl mx-auto px-4 sm:px-6 lg:px-8 py-6 space-y-6"
        dir={isAr ? 'rtl' : 'ltr'}
        data-testid="qr-stats-root"
      >
        {loading ? (
          <div className="flex items-center justify-center py-20">
            <Loader2 className="w-8 h-8 text-white/40 animate-spin" />
          </div>
        ) : stats ? (
          <>
            {/* KPI cards */}
            <div className="grid grid-cols-2 lg:grid-cols-4 gap-4">
              <KPI
                icon={QrCode}
                color="#d4af37"
                label={t('إجمالي الرموز', 'Total codes')}
                value={stats.total_qrs}
              />
              <KPI
                icon={ScanLine}
                color="#10b981"
                label={t('إجمالي المسحات', 'Total scans')}
                value={stats.total_scans}
              />
              <KPI
                icon={CalendarDays}
                color="#3b82f6"
                label={t('اليوم', 'Today')}
                value={stats.scans_today}
              />
              <KPI
                icon={TrendingUp}
                color="#f59e0b"
                label={t('آخر 7 أيام', 'Last 7 days')}
                value={stats.scans_this_week}
              />
            </div>

            {/* Daily scans chart */}
            <div className="bg-white/[0.02] border border-white/10 rounded-2xl p-6">
              <div className="flex items-center gap-2 mb-4">
                <BarChart3 className="w-4 h-4 text-[#d4af37]" />
                <h3 className="text-white font-bold text-sm">
                  {t('المسحات اليومية (آخر 30 يوم)', 'Daily scans (last 30 days)')}
                </h3>
              </div>
              {stats.daily_scans.length === 0 ? (
                <p className="text-white/40 text-sm text-center py-10">
                  {t('لا توجد مسحات بعد', 'No scans yet')}
                </p>
              ) : (
                <div className="flex items-end gap-1 h-40 overflow-x-auto pb-2">
                  {stats.daily_scans.map((d) => (
                    <div key={d.date} className="flex flex-col items-center min-w-[24px] group">
                      <div className="text-[9px] text-white/50 mb-1 group-hover:text-white transition-colors">
                        {d.count}
                      </div>
                      <div
                        className="w-full bg-gradient-to-t from-[#d4af37] to-[#f0d060] rounded-t"
                        style={{ height: `${(d.count / maxDailyCount) * 100}%`, minHeight: 2 }}
                        title={`${d.date}: ${d.count}`}
                      />
                      <div className="text-[8px] text-white/30 mt-1 transform -rotate-45 whitespace-nowrap">
                        {d.date.slice(5)}
                      </div>
                    </div>
                  ))}
                </div>
              )}
            </div>

            {/* Top 10 QRs */}
            <div className="bg-white/[0.02] border border-white/10 rounded-2xl p-6">
              <div className="flex items-center gap-2 mb-4">
                <Trophy className="w-4 h-4 text-[#d4af37]" />
                <h3 className="text-white font-bold text-sm">
                  {t('أفضل 10 رموز حسب عدد المسحات', 'Top 10 codes by scans')}
                </h3>
              </div>
              {stats.top_qrs.length === 0 ? (
                <p className="text-white/40 text-sm text-center py-10">
                  {t('لا توجد بيانات', 'No data')}
                </p>
              ) : (
                <div className="space-y-2">
                  {stats.top_qrs.map((qr, idx) => {
                    const pct = stats.top_qrs[0].scan_count
                      ? (qr.scan_count / stats.top_qrs[0].scan_count) * 100
                      : 0;
                    return (
                      <motion.button
                        key={qr.id}
                        initial={{ opacity: 0, x: -8 }}
                        animate={{ opacity: 1, x: 0 }}
                        transition={{ delay: 0.03 * idx }}
                        onClick={() => navigate(`/qr-codes/${qr.id}/stats`)}
                        className="w-full flex items-center gap-3 p-3 rounded-lg bg-white/[0.02] hover:bg-white/[0.05] border border-white/5 transition-colors text-start"
                        data-testid={`qr-top-${qr.short_code}`}
                      >
                        <span
                          className={`flex items-center justify-center w-8 h-8 rounded-lg text-xs font-bold ${
                            idx === 0 ? 'bg-[#d4af37] text-black' :
                            idx === 1 ? 'bg-white/20 text-white' :
                            idx === 2 ? 'bg-orange-500/40 text-white' :
                            'bg-white/5 text-white/60'
                          }`}
                        >
                          {idx + 1}
                        </span>
                        <div className="flex-1 min-w-0">
                          <div className="flex items-baseline gap-2">
                            <span className="text-white font-bold text-sm truncate">{qr.label}</span>
                            <span className="text-[10px] text-[#d4af37] font-mono">{qr.short_code}</span>
                          </div>
                          <div className="text-[10px] text-white/40 truncate">{qr.platform} · {qr.target_url}</div>
                          <div className="mt-1.5 h-1.5 rounded-full bg-white/5 overflow-hidden">
                            <div
                              className="h-full bg-gradient-to-r from-[#d4af37] to-[#f0d060]"
                              style={{ width: `${pct}%` }}
                            />
                          </div>
                        </div>
                        <span className="text-[#d4af37] font-bold text-lg">{qr.scan_count}</span>
                      </motion.button>
                    );
                  })}
                </div>
              )}
            </div>
          </>
        ) : null}
      </section>
    </DashboardLayout>
  );
}

// ─── KPI Card ─────────────────────────────────────────
function KPI({ icon: Icon, color, label, value }) {
  return (
    <motion.div
      initial={{ opacity: 0, y: 8 }}
      animate={{ opacity: 1, y: 0 }}
      className="bg-white/[0.02] border border-white/10 rounded-2xl p-5"
    >
      <div className="flex items-center gap-3 mb-2">
        <div
          className="w-10 h-10 rounded-xl flex items-center justify-center border"
          style={{ backgroundColor: `${color}15`, borderColor: `${color}30` }}
        >
          <Icon className="w-5 h-5" style={{ color }} />
        </div>
        <span className="text-white/50 text-xs">{label}</span>
      </div>
      <div className="text-3xl font-bold text-white">{value.toLocaleString()}</div>
    </motion.div>
  );
}
